import { Check, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { AutoSaveStatus } from '@/hooks/use-auto-save';

interface SaveStatusIndicatorProps {
    status: AutoSaveStatus;
    lastSavedAt: Date | null;
    retryCount: number;
    onSaveNow: () => void;
}

function formatTimeSince(date: Date): string {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
}

export function SaveStatusIndicator({
    status,
    lastSavedAt,
    retryCount,
    onSaveNow,
}: SaveStatusIndicatorProps) {
    if (status === 'idle') return null;

    if (status === 'pending') {
        return (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-yellow-500 animate-pulse" />
                Unsaved changes
            </div>
        );
    }

    if (status === 'saving') {
        return (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving...
            </div>
        );
    }

    if (status === 'saved') {
        return (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Check className="h-3 w-3 text-green-500" />
                Saved {lastSavedAt ? formatTimeSince(lastSavedAt) : ''}
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="flex items-center gap-1.5 text-xs text-destructive">
                <AlertTriangle className="h-3 w-3" />
                {retryCount > 0 ? `Retrying (${retryCount}/3)...` : 'Save failed'}
                {retryCount === 0 && (
                    <Button variant="ghost" size="sm" className="h-5 px-1.5 text-xs" onClick={onSaveNow}>
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Retry
                    </Button>
                )}
            </div>
        );
    }

    if (status === 'conflict') {
        return (
            <div className="flex items-center gap-1.5 text-xs text-orange-500">
                <AlertTriangle className="h-3 w-3" />
                Conflict detected — reload to continue
            </div>
        );
    }

    return null;
}
